import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../api/client.js'
import { getSocket } from '../socket/client.js'
import { timestampMs } from '../utils/timezone.js'
import type { HandoffEvent } from '../modules/chat-shared/timeline.js'

export interface InstagramChat {
  id: string
  sender_id: string
  sender_name: string | null
  sender_profile_pic: string | null
  ig_business_account_id: string
  assigned_agent_id: string | null
  assigned_at: string | null
  ai_assigned?: boolean
  ai_agent_name?: string | null
  status: string
  last_message_text: string | null
  last_message_at: string
  last_message_direction: string
  unread_count: number
  is_read: boolean
  created_at: string
  updated_at: string
}

export interface InstagramChatMessage {
  id: string
  chat_id: string
  sender_id: string
  direction: string
  content_text: string | null
  external_id: string | null
  status: string
  conversation_id: string | null
  agent_user_id: string | null
  agent_name: string | null
  sent_at: string
  created_at: string
}

export interface InstagramConversationRef {
  id: string
  reference_id: string
  status: string
  subject: string | null
  is_archived: boolean
}

export interface PendingInstagramMessage {
  localId: string
  content_text: string | null
  status: 'sending' | 'failed'
  sent_at: string
}

export function useInstagramChats(search?: string) {
  const [chats, setChats] = useState<InstagramChat[]>([])
  const [loading, setLoading] = useState(true)
  const bumpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const fetchChats = useCallback(async () => {
    try {
      const qs = search ? `?search=${encodeURIComponent(search)}` : ''
      const res = await api.get<{ chats: InstagramChat[] }>(`/agent/instagram-chats${qs}`)
      setChats(res.chats ?? [])
    } catch {
      // ignore — next bump retries
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    void fetchChats()
    const socket = getSocket()
    const onBump = (payload: { channel?: string }) => {
      if (payload.channel !== 'instagram') return
      if (bumpTimerRef.current) clearTimeout(bumpTimerRef.current)
      bumpTimerRef.current = setTimeout(() => void fetchChats(), 400)
    }
    socket.on('chat:list:bump', onBump)
    return () => {
      if (bumpTimerRef.current) clearTimeout(bumpTimerRef.current)
      socket.off('chat:list:bump', onBump)
    }
  }, [fetchChats])

  const markRead = useCallback(async (chatId: string) => {
    try {
      await api.post(`/agent/instagram-chats/${chatId}/read`)
      setChats((prev) =>
        prev.map((c) => (c.id === chatId ? { ...c, is_read: true, unread_count: 0 } : c)),
      )
    } catch {
      // ignore
    }
  }, [])

  return { chats, loading, fetchChats, markRead }
}

export function useInstagramChatMessages(chatId: string | null) {
  const [messages, setMessages] = useState<InstagramChatMessage[]>([])
  const [chat, setChat] = useState<InstagramChat | null>(null)
  const [conversations, setConversations] = useState<InstagramConversationRef[]>([])
  const [handoffEvents, setHandoffEvents] = useState<HandoffEvent[]>([])
  const [pending, setPending] = useState<PendingInstagramMessage[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const [replyWindowGuard, setReplyWindowGuard] = useState(false)
  const beforeRef = useRef<string | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const messagesRef = useRef<InstagramChatMessage[]>(messages)
  const chatRef = useRef<InstagramChat | null>(chat)
  chatRef.current = chat

  const mergeMessages = useCallback((prev: InstagramChatMessage[], next: InstagramChatMessage[]) => {
    const byId = new Map<string, InstagramChatMessage>()
    for (const m of prev) byId.set(m.id, m)
    for (const m of next) byId.set(m.id, m)
    return Array.from(byId.values()).sort((a, b) => timestampMs(a.sent_at) - timestampMs(b.sent_at))
  }, [])

  const fetchMessages = useCallback(async () => {
    if (!chatId) return
    try {
      const res = await api.get<{
        chat: InstagramChat
        messages: InstagramChatMessage[]
        hasMore: boolean
        before?: string
        conversations: InstagramConversationRef[]
        handoff_events?: HandoffEvent[]
        reply_window_guard?: boolean
      }>(`/agent/instagram-chats/${chatId}?limit=20`)
      setChat(res.chat ?? null)
      chatRef.current = res.chat ?? null
      setMessages((prev) => mergeMessages(prev, res.messages ?? []))
      setHasMore(res.hasMore ?? false)
      if (messagesRef.current.length === 0) beforeRef.current = res.before ?? null
      setConversations(res.conversations ?? [])
      setHandoffEvents(res.handoff_events ?? [])
      setReplyWindowGuard(res.reply_window_guard === true)
    } catch {
      // ignore
    } finally {
      setLoading(false)
    }
  }, [chatId, mergeMessages])

  const loadMore = useCallback(async () => {
    if (!chatId || beforeRef.current === null || !hasMore || loadingMore) return
    setLoadingMore(true)
    try {
      const res = await api.get<{
        messages: InstagramChatMessage[]
        hasMore: boolean
        before?: string
      }>(`/agent/instagram-chats/${chatId}?before=${encodeURIComponent(beforeRef.current)}&limit=20`)
      setMessages((prev) => [...(res.messages ?? []), ...prev])
      setHasMore(res.hasMore ?? false)
      beforeRef.current = res.before ?? null
    } catch {
      // ignore
    } finally {
      setLoadingMore(false)
    }
  }, [chatId, hasMore, loadingMore])

  useEffect(() => {
    if (!chatId) {
      setMessages([])
      setChat(null)
      chatRef.current = null
      setConversations([])
      setHandoffEvents([])
      setPending([])
      return
    }
    setLoading(true)
    setLoadingMore(false)
    setHasMore(false)
    beforeRef.current = null
    setPending([])
    void fetchMessages()

    const socket = getSocket()
    const onBump = (payload: { channel?: string; conversationId?: string }) => {
      if (payload.channel !== 'instagram') return
      const targetMatches =
        !payload.conversationId ||
        payload.conversationId === chatId ||
        (chatRef.current && (payload.conversationId === chatRef.current.sender_id || payload.conversationId === chatRef.current.id))
      if (targetMatches) {
        void fetchMessages()
      }
    }
    const onReconnect = () => void fetchMessages()

    socket.on('chat:list:bump', onBump)
    socket.on('connect', onReconnect)

    // Dynamic fallback: 15s when WebSocket is active, 4s if disconnected or reconnecting
    const setupTimer = () => {
      if (pollRef.current) clearInterval(pollRef.current)
      const intervalMs = socket.connected ? 15000 : 4000
      pollRef.current = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState === 'visible') {
          void fetchMessages()
        }
      }, intervalMs)
    }
    setupTimer()
    socket.on('connect', setupTimer)
    socket.on('disconnect', setupTimer)

    return () => {
      socket.off('chat:list:bump', onBump)
      socket.off('connect', onReconnect)
      socket.off('connect', setupTimer)
      socket.off('disconnect', setupTimer)
      if (pollRef.current) clearInterval(pollRef.current)
    }
  }, [chatId, fetchMessages])

  const dismissPending = useCallback((localId: string) => {
    setPending((prev) => prev.filter((p) => p.localId !== localId))
  }, [])

  const sendMessage = useCallback(
    async (text: string) => {
      if (!chatId || !text.trim()) return
      const localId = `pending-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
      setPending((prev) => [
        ...prev,
        { localId, content_text: text.trim(), status: 'sending', sent_at: new Date().toISOString() },
      ])
      try {
        await api.post(`/agent/instagram-chats/${chatId}/reply`, { text })
        await fetchMessages()
        setPending((prev) => prev.filter((p) => p.localId !== localId))
      } catch (err) {
        setPending((prev) =>
          prev.map((p) => (p.localId === localId ? { ...p, status: 'failed' } : p)),
        )
        console.error('Failed to send Instagram message:', err)
        throw err
      }
    },
    [chatId, fetchMessages],
  )

  const createTicket = useCallback(
    async (messageIds?: string[], subject?: string) => {
      if (!chatId) return null
      try {
        const res = await api.post<{ conversationId: string; referenceId: string }>(
          `/agent/instagram-chats/${chatId}/create-ticket`,
          {
            ...(messageIds && messageIds.length > 0 ? { messageIds } : {}),
            ...(subject ? { subject } : {}),
          },
        )
        await fetchMessages()
        return res
      } catch (err) {
        console.error('Failed to create conversation from Instagram chat:', err)
        throw err
      }
    },
    [chatId, fetchMessages],
  )

  return {
    chat,
    messages,
    handoffEvents,
    replyWindowGuard,
    conversations,
    pending,
    loading,
    loadingMore,
    hasMore,
    sendMessage,
    fetchMessages,
    createTicket,
    dismissPending,
    loadMore,
  }
}
